import {
  ArrowLeft,
  CheckCircle2,
  CircleDot,
  Database,
  FileText,
  Loader2,
  Server,
  Wrench,
  Workflow,
} from "lucide-react";
import type { ComponentType } from "react";
import { Link, useParams } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeed } from "@/hooks/useFeed";
import { useGantt } from "@/hooks/useGantt";
import { useModuli } from "@/hooks/useModuli";
import { cn } from "@/lib/utils";
import type { FeedTipo, ModuloStato, VoceFeed } from "@/types/api";

import { NotFoundPage } from "./NotFoundPage";

const FEED_ICON: Record<FeedTipo, ComponentType<{ className?: string }>> = {
  completato: CheckCircle2,
  in_corso: Loader2,
  fix: Wrench,
  dati: Database,
  documenti: FileText,
  infra: Server,
  altro: CircleDot,
};

const STATO_LABEL: Record<ModuloStato, string> = {
  roadmap: "in roadmap",
  anteprima: "anteprima",
  beta: "beta",
  live: "live",
  dismesso: "dismesso",
};

const STATO_DOT: Record<ModuloStato, string> = {
  roadmap: "bg-muted-foreground/40",
  anteprima: "bg-warning",
  beta: "bg-primary",
  live: "bg-success",
  dismesso: "bg-muted-foreground/20",
};

function formatDataIt(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function ModuloDettaglioPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: moduli = [], isLoading: loadingModuli } = useModuli();
  const { data: ganttElementi = [] } = useGantt();
  const { data: feed = [] } = useFeed({ limit: 50 });

  if (loadingModuli) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-32 w-full" />
      </PageContainer>
    );
  }

  const modulo = moduli.find((m) => m.slug === slug);
  if (!modulo) return <NotFoundPage />;

  const elementiModulo = ganttElementi
    .filter((g) => g.modulo_id === modulo.id)
    .sort((a, b) => a.data_inizio.localeCompare(b.data_inizio));
  const feedModulo = feed.filter((v) => v.modulo_id === modulo.id);

  return (
    <PageContainer>
      <Link
        to="/moduli"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Tutti i moduli
      </Link>

      <header className="space-y-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="font-mono text-sm text-muted-foreground">{modulo.codice}</span>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">{modulo.nome}</h1>
          <Badge variant="outline">
            <span className={cn("size-1.5 rounded-full", STATO_DOT[modulo.stato])} />
            {STATO_LABEL[modulo.stato]}
          </Badge>
        </div>
        {modulo.sottotitolo && (
          <p className="text-base text-muted-foreground">{modulo.sottotitolo}</p>
        )}
        {modulo.descrizione_estesa && (
          <p className="text-sm text-foreground/80 leading-relaxed max-w-3xl whitespace-pre-line">
            {modulo.descrizione_estesa}
          </p>
        )}

        {(modulo.data_inizio_prevista ||
          modulo.data_fine_costruzione ||
          modulo.data_go_live ||
          modulo.owner_cliente_label) && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground pt-2 border-t">
            {modulo.data_inizio_prevista && (
              <span>
                Inizio:{" "}
                <span className="font-medium text-foreground">
                  {formatDataIt(modulo.data_inizio_prevista)}
                </span>
              </span>
            )}
            {modulo.data_fine_costruzione && (
              <span>
                Fine costruzione:{" "}
                <span className="font-medium text-foreground">
                  {formatDataIt(modulo.data_fine_costruzione)}
                </span>
              </span>
            )}
            {modulo.data_go_live && (
              <span>
                Go-live:{" "}
                <span className="font-medium text-foreground">
                  {formatDataIt(modulo.data_go_live)}
                </span>
              </span>
            )}
            {modulo.owner_cliente_label && (
              <span>
                Referente:{" "}
                <span className="font-medium text-foreground">{modulo.owner_cliente_label}</span>
              </span>
            )}
          </div>
        )}
      </header>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Timeline</h2>
        {elementiModulo.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun elemento Gantt associato.</p>
        ) : (
          <ul className="rounded-lg border bg-card divide-y">
            {elementiModulo.map((el) => (
              <li
                key={el.id}
                className="flex items-center gap-4 px-4 py-3 text-sm"
              >
                <Badge variant="outline" className="font-mono text-[10px] uppercase shrink-0">
                  {el.tipo === "barra_costruzione"
                    ? "build"
                    : el.tipo === "barra_uso"
                    ? "uso"
                    : el.tipo === "punto_milestone"
                    ? "milestone"
                    : "banda"}
                </Badge>
                <span className="font-medium flex-1 truncate">{el.etichetta}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDataIt(el.data_inizio)}
                  {el.data_fine && ` → ${formatDataIt(el.data_fine)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Attività recenti</h2>
        {feedModulo.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nessuna voce di attività ancora associata a questo modulo.
          </p>
        ) : (
          <ul className="rounded-lg border bg-card divide-y">
            {feedModulo.map((v) => (
              <FeedRowFull key={v.id} voce={v} />
            ))}
          </ul>
        )}
      </section>

      <EmptyState
        icon={Workflow}
        title="Dati operativi in arrivo"
        description="Quando il modulo entrerà in fase di anteprima/beta, in questa pagina compariranno i pannelli operativi specifici (ordini, anagrafiche, output AI, KPI) con visibilità progressiva."
      />
    </PageContainer>
  );
}

function FeedRowFull({ voce }: { voce: VoceFeed }) {
  const Icon = FEED_ICON[voce.tipo];
  return (
    <li className="flex gap-3 items-start px-4 py-3 text-sm">
      <Icon
        className={cn(
          "size-4 shrink-0 mt-0.5 text-muted-foreground",
          voce.tipo === "in_corso" && "animate-spin",
          voce.tipo === "completato" && "text-success"
        )}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium leading-snug">{voce.titolo_pubblicato}</p>
        {voce.corpo_pubblicato && (
          <p className="text-xs text-muted-foreground leading-relaxed">{voce.corpo_pubblicato}</p>
        )}
      </div>
      {/* Data del lavoro, non della pubblicazione (vedi `api/v1/feed.py`). */}
      <span className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0 mt-0.5">
        {formatDataIt(voce.git_committed_at)}
      </span>
    </li>
  );
}
